'use client';

import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import styles from './cms-workspace-sidebar-tools.module.css';

export function SidebarPopover({
  label,
  trigger,
  children,
  className,
  popoverClassName,
}: {
  label: string;
  trigger: ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  className?: string;
  popoverClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const shell = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const pinned = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();
  const cancelTimer = () => {
    if (timer.current) clearTimeout(timer.current);
  };
  const close = () => {
    cancelTimer();
    pinned.current = false;
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!shell.current?.contains(event.target as Node)) {
        pinned.current = false;
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  return (
    <div
      ref={shell}
      className={styles.popoverShell}
      onPointerEnter={(event) => {
        cancelTimer();
        if (event.pointerType === 'mouse')
          timer.current = setTimeout(() => setOpen(true), 180);
      }}
      onPointerLeave={(event) => {
        cancelTimer();
        if (event.pointerType === 'mouse' && !pinned.current)
          timer.current = setTimeout(() => setOpen(false), 180);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          close();
          button.current?.focus();
        }
      }}
    >
      <button
        ref={button}
        type="button"
        className={className}
        aria-label={label}
        title={label}
        aria-expanded={open}
        aria-controls={id}
        aria-haspopup="dialog"
        onClick={() => {
          cancelTimer();
          if (pinned.current) close();
          else {
            pinned.current = true;
            setOpen(true);
          }
        }}
      >
        {trigger}
      </button>
      <dialog
        id={id}
        open={open}
        aria-label={label}
        className={`${styles.popover} ${popoverClassName ?? ''}`}
      >
        {typeof children === 'function'
          ? children(() => {
              close();
              button.current?.focus();
            })
          : children}
      </dialog>
    </div>
  );
}
